<template>
  <b-container fluid>
    <b-row no-gutters class="bordered-box-header align-items-center">
      <h2>{{pageTitle}}</h2>
    </b-row>
    <b-row no-gutters class="bordered-box">
      <b-form @submit.prevent="onSubmit" class="col-12">
        <b-form-group class="required" label="Wyrażenie">
          <b-form-input
            v-model="term"
            type="text"
            autocomplete="off"
            :state="$v.term.$error? false : null"
            @input="$v.term.$reset()"
          />
          <b-form-invalid-feedback>
            <div v-if="!$v.term.required">To pole jest wymagane</div>
          </b-form-invalid-feedback>
        </b-form-group>

        <b-form-group class="text-right">
          <b-button
            variant="outline-primary"
            class="btn-sm"
            @click="downloadAudio(term, partOfSpeech)"
          >
            <font-awesome-icon icon="download" class="fa-fw"></font-awesome-icon>&nbsp; Pobierz ze słownika LEXICO (Oxford)
          </b-button>
        </b-form-group>

        <b-form-group label="Część mowy" class>
          <b-form-select v-model="partOfSpeech" :options="partOfSpeechOptions" />
        </b-form-group>

        <b-form-group class="required" label="Tłumaczenie">
          <b-form-input
            v-model="translation"
            type="text"
            autocomplete="off"
            :state="$v.translation.$error? false : null"
            @input="$v.translation.$reset()"
          />
          <b-form-invalid-feedback>
            <div v-if="!$v.translation.required">To pole jest wymagane</div>
          </b-form-invalid-feedback>
        </b-form-group>

        <b-form-group class="required" label="Wymowa">
          <b-form-input
            v-model="pronunciation"
            type="text"
            autocomplete="off"
            :state="$v.pronunciation.$error? false : null"
            @input="$v.pronunciation.$reset()"
          />
          <b-form-invalid-feedback>
            <div v-if="!$v.pronunciation.required">To pole jest wymagane</div>
          </b-form-invalid-feedback>
        </b-form-group>

        <b-form-group class="required" label="Przykładowe zdanie">
          <b-form-input
            v-model="exampleSentence"
            type="text"
            autocomplete="off"
            :state="$v.exampleSentence.$error? false : null"
            @input="$v.exampleSentence.$reset()"
          />
          <b-form-invalid-feedback>
            <div v-if="!$v.exampleSentence.required">To pole jest wymagane</div>
          </b-form-invalid-feedback>
        </b-form-group>

        <b-form-group class="required" label="Zdanie do wypełnienia (ćwiczenie)">
          <b-form-input
            v-model="exerciseSentence"
            type="text"
            autocomplete="off"
            :state="$v.exerciseSentence.$error? false : null"
            @input="$v.exerciseSentence.$reset()"
          />
          <b-form-invalid-feedback>
            <div v-if="!$v.exerciseSentence.required">To pole jest wymagane</div>
            <div
              v-if="!$v.exerciseSentence.mustContainBlank"
            >Zdanie musi zawierać przynajmniej jeden placeholder {wyraz} gdzie wyraz pomiędzy klamrami to poprawna odpowiedź lub jej część.</div>
          </b-form-invalid-feedback>
        </b-form-group>

        <b-form-group>
          <b-form-checkbox
            switch
            v-model="caseSensitive"
            @change="$v.$reset()"
          >Uwzględniaj wielkość znaków przy wykonywaniu ćwiczeń</b-form-checkbox>
        </b-form-group>

        <b-form-group label="Audio" class="audioGroup d-flex flex-column">
          <div v-if="audioFileId" class="flex-row">
            <div style="margin-bottom: 4px;">
              <audio controls controlslist="nodownload">
                <source :src="audioFileUrl" />Twoja przeglądarka nie wspiera elementu audio.
              </audio>
            </div>
            <div>
              <button class="btn btn-sm btn-outline-info" @click.prevent="download(audioFileId)">
                <font-awesome-icon icon="download" class="fa-fw"></font-awesome-icon>
              </button>
              <button class="btn btn-sm btn-outline-danger" @click.prevent="audioFileId = null">
                <font-awesome-icon icon="trash" class="fa-fw"></font-awesome-icon>
              </button>
            </div>
          </div>
          <div class="flex-row">
            <file-select v-model="audioFile" @input="(f) => audioFile = f" accept=".mp3"></file-select>
            <b-button
              variant="outline-primary"
              v-if="audioFile"
              class="btn-sm btn-submit-file"
              v-on:click.prevent="submitFile(audioFile, r => { audioFileId = r.data.id; audioFile = null; })"
            >Submit</b-button>
          </div>
        </b-form-group>
        <b-form-group label="Obraz" class="imageGroup d-flex flex-column">
          <div v-if="imageFileId" class="flex-row">
            <img :src="imageFileUrl" style="max-width: 600px; padding-bottom: 8px;" />
            <br />
            <button class="btn btn-sm btn-outline-info" @click.prevent="download(imageFileId)">
              <font-awesome-icon icon="download" class="fa-fw"></font-awesome-icon>
            </button>
            <button class="btn btn-sm btn-outline-danger" @click.prevent="imageFileId = null">
              <font-awesome-icon icon="trash" class="fa-fw"></font-awesome-icon>
            </button>
          </div>
          <div class="flex-row">
            <file-select v-model="imageFile" @input="(f) => imageFile = f" accept=".jpg, .jpeg"></file-select>
            <b-button
              variant="outline-primary"
              v-if="imageFile"
              class="btn-sm btn-submit-file"
              v-on:click.prevent="submitFile(imageFile, r => { imageFileId = r.data.id; imageFile = null; })"
            >Submit</b-button>
          </div>
        </b-form-group>

        <b-form-group class="text-right">
          <hr />
          <b-button
            type="submit"
            variant="primary"
            class="mr-1"
            :disabled="saving"
            @click="$v.$touch"
          >
            <span v-show="!saving">Zapisz</span>
            <span v-show="saving">
              <b-spinner small label="Proszę czekać..."></b-spinner>
            </span>
          </b-button>
          <b-button class="mr-1" variant="secondary" @click="$router.go(-1)">Powrót</b-button>
        </b-form-group>
      </b-form>
    </b-row>
  </b-container>
</template>

<script>
import { axios, eventBus } from "@/_infrastructure";
import { required } from "vuelidate/lib/validators";
import FileSelect from "@/components/FileSelect.vue";

function findIndices(str, ch) {
  let indices = [];

  if (str) {
    for (let i = 0; i < str.length; i++) {
      if (str[i] === ch) indices.push(i);
    }
  }
  return indices;
}

const mustContainBlank = value => {
  var openingBracketsIndices = findIndices(value, "{");
  var closingBracketsIndices = findIndices(value, "}");

  var result =
    !value ||
    (openingBracketsIndices.length &&
      openingBracketsIndices.length == closingBracketsIndices.length);

  let allBracketsIndicesSequence = [];
  for (var i in openingBracketsIndices) {
    allBracketsIndicesSequence.push(openingBracketsIndices[i]);
    allBracketsIndicesSequence.push(closingBracketsIndices[i]);
  }

  for (var i in allBracketsIndicesSequence) {
    if (i < allBracketsIndicesSequence.length - 1) {
      let index = parseInt(i);

      result &=
        allBracketsIndicesSequence[index] <
        allBracketsIndicesSequence[index + 1];
    }
  }

  return result;
};

export default {
  components: { FileSelect },
  data() {
    return {
      wordId: this.id,
      saving: false,
      audioFile: null,
      imageFile: null,
      term: null,
      translation: null,
      pronunciation: null,
      partOfSpeech: null,
      partOfSpeechOptions: [
        { value: null, text: "" },
        { value: 1, text: "czasownik regularny" },
        { value: 2, text: "czasownik nieregularny" },
        { value: 6, text: "czasownik frazalny" },
        { value: 7, text: "idiom" },
        { value: 3, text: "przymiotnik" },
        { value: 4, text: "przysłówek" },
        { value: 0, text: "rzeczownik" },
        { value: 8, text: "wyrażenie" },
        { value: 5, text: "zaimek" }
      ],
      exampleSentence: null,
      caseSensitive: false,
      exerciseSentence: null,
      audioFileId: null,
      imageFileId: null
    };
  },
  props: ["id"],
  validations: {
    term: {
      required
    },
    translation: {
      required
    },
    pronunciation: {
      required
    },
    exampleSentence: {
      required
    },
    exerciseSentence: {
      required,
      mustContainBlank
    }
  },
  computed: {
    pageTitle() {
      if (this.wordId) {
        return "Edycja wyrazu";
      } else {
        return "Utwórz nowy wyraz";
      }
    },
    imageFileUrl() {
      return axios.getFileUrl(this.imageFileId);
    },
    audioFileUrl() {
      return axios.getFileUrl(this.audioFileId);
    }
  },
  mounted() {
    if (this.id) {
      let loader = this.$loading.show({
        container: this.$refs.mainContainer
      });
      axios
        .get("/words/details", {
          params: {
            id: this.id
          }
        })
        .then(response => {
          var result = response.data;
          this.term = result.term;
          this.caseSensitive = result.caseSensitive;
          this.translation = result.translation;
          this.pronunciation = result.pronunciation;
          this.audioFileId = result.audioId;
          this.imageFileId = result.imageId;
          this.exampleSentence = result.exampleSentence;
          this.exerciseSentence = result.exerciseSentence;
          this.partOfSpeech = result.partOfSpeech;
        })
        .catch(error => eventBus.$emit("unhandled-error", error))
        .then(() => {
          loader.hide();
        });
    }
  },
  methods: {
    goto(url) {
      this.$router.push(url);
    },
    downloadAudio(term, partOfSpeech) {
      axios
        .get("/words/audio", {
          responseType: "blob",
          params: {
            term: term,
            partOfSpeech: partOfSpeech
          }
        })
        .then(response => {
          var result = response.data;
          const url = window.URL.createObjectURL(new Blob([result]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", `${term}.mp3`);

          document.body.appendChild(link);
          link.click();
        })
        .catch(() =>
          eventBus.$emit(
            "unhandled-error",
            "Nie udało się pobrać pliku audio ze słownika"
          )
        )
        .then(() => {});
    },
    async download(fileId) {
      if (fileId) {
        var fileDtoResponse = await axios.get("/files", {
          params: {
            id: fileId
          }
        });

        let fileDto = fileDtoResponse.data;
        window.console.log(fileDto);
        if (fileDto) {
          let result = await axios.downloadFileAsync(fileId);

          window.console.log(result);

          const url = window.URL.createObjectURL(new Blob([result]));
          const link = document.createElement("a");
          link.href = url;

          link.setAttribute("download", fileDto.fullName);

          document.body.appendChild(link);
          link.click();
        }
      }
    },
    submitFile(file, callback) {
      let loader = this.$loading.show({
        container: this.$refs.mainContainer
      });

      let formData = new FormData();
      formData.append("file", file);
      axios
        .post("/files", formData, {
          headers: {
            "Content-Type": "multipart/form-data"
          }
        })
        .then(result => {
          callback(result);
        })
        .catch(error => eventBus.$emit("unhandled-error", error))
        .then(() => loader.hide());
    },

    onSubmit() {
      if (!this.$v.$error) {
        this.saving = true;

        axios[this.wordId ? "put" : "post"]("/words", {
          id: this.wordId,
          caseSensitive: this.caseSensitive,
          term: this.term,
          translation: this.translation,
          pronunciation: this.pronunciation,
          partOfSpeech: this.partOfSpeech,
          exampleSentence: this.exampleSentence,
          exerciseSentence: this.exerciseSentence,
          exercise: this.exercise,
          imageId: this.imageFileId,
          audioId: this.audioFileId
        })
          .then(response => {
            var result = response.data;

            this.wordId = result;
            eventBus.$emit("notify-success", "Zapisano poprawnie.");
          })
          .catch(error => eventBus.$emit("unhandled-error", error))
          .then(() => (this.saving = false));
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.bordered-box-header {
  margin-top: 20px;
  padding: 10px;
  margin-bottom: 0px;
  background: rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(0, 0, 0, 0.125);
}

.bordered-box {
  border: 1px solid rgba(0, 0, 0, 0.125);
  padding: 10px;
}

.audioGroup {
  div.flex-row {
    margin-bottom: 10px;
  }

  margin-bottom: 0px;

  button {
    margin-right: 5px;
    border-radius: 5px;
  }
}
</style>